import { Component, Input, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MfaService } from '@services/auth/mfa.service';
import { ConfirmDialogService } from '@services/ui/confirm-dialog.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { ToggleSwitchComponent } from '@shared/components/toggle-switch/toggle-switch.component';
import { SharedModule } from '@shared/modules/shared.module';
import { ProfileMfaStateService } from './profile-mfa-state.service';

/**
 * 2FA row of the profile's security card: status, enable (opens the enrollment dialog, which the profile
 * renders) and disable (confirm + unenroll). The status lives in ProfileMfaStateService, provided by the profile.
 */
@Component({
	selector: 'app-profile-mfa-section',
	imports: [SharedModule, ButtonComponent, ToggleSwitchComponent],
	templateUrl: './profile-mfa-section.component.html',
	styleUrl: './profile-mfa-section.component.scss',
})
export class ProfileMfaSectionComponent {
	/** The profile is in edit mode: the toggle is locked */
	@Input() disabled = false;

	readonly mfa = inject(ProfileMfaStateService);
	private readonly mfaService = inject(MfaService);
	private readonly confirmDialogService = inject(ConfirmDialogService);
	private readonly translocoService = inject(TranslocoService);

	onToggleChange(enabled: boolean): void {
		if (enabled) {
			// Enable MFA - show enrollment dialog
			this.mfa.isEnrolling = true;
			this.mfa.showDialog = true;
		} else {
			// Disable MFA - confirm and unenroll
			this.disable();
		}
	}

	private disable(): void {
		if (!this.mfa.factorId) {
			return;
		}

		const factorId = this.mfa.factorId;

		this.confirmDialogService.confirmAndExecute(
			'disable',
			this.translocoService.translate('profile.mfa.title'),
			() => this.mfaService.unenroll(factorId),
			{
				confirmMessage: this.translocoService.translate('profile.mfa.confirmDisable'),
				header: this.translocoService.translate('profile.mfa.confirmDisableHeader'),
				successMessage: this.translocoService.translate('profile.mfa.disabledSuccess'),
				errorMessage: this.translocoService.translate('profile.mfa.errorDisabling'),
				onSuccess: () => this.mfa.load(),
				onError: () => {
					// Revert toggle on error
					this.mfa.enabled = true;
				},
			},
		);

		// Revert toggle immediately - will be set correctly by load() on success
		// or kept as true on cancel/error
		this.mfa.enabled = true;
	}
}
